export type TelemetryPeriod = 'hourly' | 'daily' | 'monthly';

export interface PortalPayloadContext {
  url: string;
  period: TelemetryPeriod | null;
  dateIso: string;
  placeId: string;
}

/**
 * The proxy stamps each captured page with its source URL (AI_RULES.md Rule 8.2) so the parser
 * can recover the period instead of guessing from page text — nav links to every other period
 * endpoint are present on every page, which made document-substring checks misclassify charts.
 */
const URL_MARKER_RE = /<!--\s*URL:\s*([^\s>]+?)\s*-->/i;

export function readPayloadUrl(rawSource: string): string {
  const match = rawSource?.match(URL_MARKER_RE);
  return match ? match[1] : '';
}

export function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

/**
 * Portal pages are JST calendar pages, so the fallback anchor for year-less ticks must be JST too
 * (AI_RULES.md Rule 8.3) — a UTC "today" is off by one for any request before 09:59 JST.
 */
export function jstTodayIso(now: number = Date.now()): string {
  const jst = new Date(now + 9 * 60 * 60 * 1000);
  return `${jst.getUTCFullYear()}-${pad2(jst.getUTCMonth() + 1)}-${pad2(jst.getUTCDate())}`;
}

export function contextFromUrl(url: string): PortalPayloadContext {
  const empty: PortalPayloadContext = { url, period: null, dateIso: '', placeId: '' };
  if (!url) return empty;

  const hourly = url.match(/\/amounts\/(\d{5,})\/hourly\/(\d{4})-(\d{1,2})-(\d{1,2})/i);
  if (hourly) {
    return {
      url,
      period: 'hourly',
      dateIso: `${hourly[2]}-${pad2(Number(hourly[3]))}-${pad2(Number(hourly[4]))}`,
      placeId: hourly[1]
    };
  }

  const daily = url.match(/\/amounts\/(\d{5,})\/daily/i);
  if (daily) return { url, period: 'daily', dateIso: '', placeId: daily[1] };

  // /symenergy/amounts/318949 hosts the 前年/今年 12-month comparison chart.
  const monthly = url.match(/\/amounts\/(\d{5,})\/?$/i);
  if (monthly) return { url, period: 'monthly', dateIso: '', placeId: monthly[1] };

  return empty;
}

/**
 * Classifies a chart from its x-axis values only. Every value must fit one period's shape, so a
 * mixed or unknown axis returns null instead of silently falling through to the daily branch.
 */
export function classifyPeriodFromXAxis(rawValues: unknown[]): TelemetryPeriod | null {
  const values = rawValues.map((v) => String(v ?? '').trim()).filter((v) => v !== '');
  if (values.length < 2) return null;

  const isHourLabel = (v: string) =>
    /^\d{1,2}:\d{2}$/.test(v) || (/^\d{1,2}$/.test(v) && Number(v) <= 23);
  if (values.every(isHourLabel)) return 'hourly';

  if (values.every((v) => /^\d{1,2}\/\d{1,2}(\(.+\))?$/.test(v))) return 'daily';

  if (values.every((v) => /^\d{1,2}月$/.test(v) || /^\d{4}年\d{1,2}月$/.test(v))) return 'monthly';

  if (values.every((v) => /^\d{4}[/年-]\d{1,2}/.test(v))) return 'monthly';

  return null;
}

export interface MonthAnchor {
  year: number;
  month: number;
}

export function monthAnchor(anchorIso: string): MonthAnchor {
  const match = /^(\d{4})-(\d{1,2})/.exec(anchorIso || '');
  if (match) return { year: Number(match[1]), month: Number(match[2]) };
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

/**
 * Portal day ticks carry no year, and a rolling window can wrap the new year, so a month far ahead
 * of the anchor month belongs to last year and a month far behind belongs to next year.
 */
export function resolveTickYear(monthNum: number, anchorIso: string): number {
  const anchor = monthAnchor(anchorIso);
  if (monthNum - anchor.month > 6) return anchor.year - 1;
  if (anchor.month - monthNum > 6) return anchor.year + 1;
  return anchor.year;
}

/**
 * Resolves the year of every tick in a month axis that omits years (e.g. the 前年/今年 chart's
 * `10月…9月`). The axis ascends chronologically and its last tick is the most recent month, so the
 * last tick anchors the current (or previous) year and every wrap backwards decrements it.
 */
export function resolveMonthTickYears(months: number[], anchorIso: string): number[] {
  if (months.length === 0) return [];
  const anchor = monthAnchor(anchorIso);
  const years = new Array<number>(months.length);

  let year = anchor.year;
  if (months[months.length - 1] > anchor.month) year -= 1;
  years[months.length - 1] = year;

  for (let i = months.length - 2; i >= 0; i--) {
    if (months[i] > months[i + 1]) year -= 1;
    years[i] = year;
  }
  return years;
}

export function buildIsoDate(year: number, month: number, day: number): string | null {
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const probe = new Date(Date.UTC(year, month - 1, day));
  if (probe.getUTCMonth() !== month - 1 || probe.getUTCDate() !== day) return null;
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

/**
 * Legacy rows were stored as `2026-${'9/1'}` -> `2026-9-1`, or as month ticks like `2026-9月` that
 * were never days. Returns the padded ISO day, or null when the value cannot be a calendar day.
 */
export function normalizeDailyTimestamp(timestamp: string | undefined): string | null {
  if (!timestamp) return null;
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(timestamp.trim());
  if (iso) return buildIsoDate(Number(iso[1]), Number(iso[2]), Number(iso[3]));
  const slash = /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/.exec(timestamp.trim());
  if (slash) return buildIsoDate(Number(slash[1]), Number(slash[2]), Number(slash[3]));
  return null;
}

export function normalizeMonthlyTimestamp(timestamp: string | undefined): string | null {
  if (!timestamp) return null;
  const iso = /^(\d{4})-(\d{1,2})$/.exec(timestamp.trim());
  if (iso) return `${iso[1]}-${pad2(Number(iso[2]))}`;
  const slash = /^(\d{4})\/(\d{1,2})$/.exec(timestamp.trim());
  if (slash) return `${slash[1]}-${pad2(Number(slash[2]))}`;
  return null;
}
